import React, { FC, useEffect, useRef, useState } from 'react'
import { Card, Col, Modal, Row, Tabs, Typography } from 'antd'
import { translate } from '../../../core'
import { ItemType } from '../../inventory/type/item'
import { PRODUCT_TYPES_IN_UI, productTypeDisplayLabel } from '../type/filter-catalog'
import { FilterConfigSidebar } from './FilterConfigSidebar'
import {
  FilterBaseConfigPanel,
  FilterBaseConfigPanelHandle,
} from './FilterBaseConfigPanel'
import { FilterOverrideRulesPanel } from './FilterOverrideRulesPanel'

const TAB_BASE = 'base'
const TAB_OVERRIDES = 'overrides'

export const FilterConfigPage: FC = () => {
  const [activeType, setActiveType] = useState<ItemType>(PRODUCT_TYPES_IN_UI[0])
  const [activeTab, setActiveTab] = useState<string>(TAB_BASE)
  const [baseDirty, setBaseDirty] = useState(false)
  const baseRef = useRef<FilterBaseConfigPanelHandle>(null)

  // Warn on full-page navigation / refresh while unsaved local edits exist.
  useEffect(() => {
    if (!baseDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [baseDirty])

  /**
   * Gate destructive UI transitions (type switch, tab switch) on the dirty
   * flag. When clean, `next` runs immediately. When dirty, the user must
   * confirm — confirming runs `reset` on the base panel so the next mount
   * starts from server state.
   */
  const guard = (next: () => void) => {
    if (!baseDirty) {
      next()
      return
    }
    Modal.confirm({
      title: translate('unsaved_changes'),
      content: translate('discard_unsaved_confirm'),
      okText: translate('discard'),
      cancelText: translate('cancel'),
      okButtonProps: { danger: true },
      onOk: () => {
        baseRef.current?.reset()
        setBaseDirty(false)
        next()
      },
    })
  }

  return (
    <Card>
      <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 20 }}>
        {translate('product_filters')}
      </Typography.Title>
      <Row gutter={24}>
        <Col xs={24} md={6} lg={5}>
          <FilterConfigSidebar
            productTypes={PRODUCT_TYPES_IN_UI}
            active={activeType}
            onChange={(next) => guard(() => setActiveType(next))}
          />
        </Col>
        <Col xs={24} md={18} lg={19}>
          <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
            {productTypeDisplayLabel(activeType)}
          </Typography.Title>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => guard(() => setActiveTab(key))}
            destroyInactiveTabPane
            items={[
              {
                key: TAB_BASE,
                label: translate('base_configuration'),
                children: (
                  <FilterBaseConfigPanel
                    ref={baseRef}
                    productType={activeType}
                    onDirtyChange={setBaseDirty}
                  />
                ),
              },
              {
                key: TAB_OVERRIDES,
                label: translate('override_rules'),
                children: <FilterOverrideRulesPanel productType={activeType} />,
              },
            ]}
          />
        </Col>
      </Row>
    </Card>
  )
}
